import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Breadcrumb, Row, Col, Space, Spin, Button, Popconfirm, } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  GlobalOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { ApiItem } from '../types/api';
import apiConfigurationService from '../services/apiConfigurations';
import ApiConfigurationModal from '../components/ApiConfigurationModal';
import { message } from '../utils/message';
import { tokenManager } from '../services/api';

const { Title, Text } = Typography;

const ApiManagement: React.FC = () => {
  // 상태 관리
  const [apiData, setApiData] = useState<ApiItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingApi, setEditingApi] = useState<ApiItem | null>(null);
  const [healthCheckData, setHealthCheckData] = useState<Map<number, boolean>>(new Map());
  const [healthCheckLoading, setHealthCheckLoading] = useState<boolean>(false);

  // 현재 사용자 정보 가져오기
  const currentUser = tokenManager.getUser();
  const isDeveloperAdmin = currentUser?.role === 'development_admin';

  // 헬스체크 수행 함수
  const performHealthCheck = useCallback(async () => {
    setHealthCheckLoading(true);
    try {
      const response = await apiConfigurationService.healthCheckAll(true); // activeOnly: true로 변경
      const healthChecks = response.data.healthChecks || [];

      // Map으로 헬스체크 결과 저장
      const healthMap = new Map<number, boolean>();
      healthChecks.forEach((check: any) => {
        healthMap.set(check.id, check.healthy);
      });

      setHealthCheckData(healthMap);
      // console.log('Health check completed:', healthMap);
    } catch (error) {
      console.error('Health check error:', error);
      message.error('헬스체크 중 오류가 발생했습니다.');
    } finally {
      setHealthCheckLoading(false);
    }
  }, []);

  // 데이터 로드 함수
  const loadApiData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiConfigurationService.getApiConfigurations({ isActive: true }); // 활성화된 API만 조회

      // 백엔드 데이터를 프론트엔드 ApiItem 형식으로 변환
      const responseData: any = response.data;
      const configurations = Array.isArray(responseData) ? responseData : (responseData.configurations || []);
      const mappedData: ApiItem[] = configurations.map((item: any) => ({
        id: item.id,
        title: item.displayName,
        description: item.description || '',
        baseUrl: item.endpointUrl || '',
        homepage: item.configJson?.homepage || '',
        category: item.category,
        isHealthy: item.isActive, // 기본값은 isActive 사용
      }));

      setApiData(mappedData);

      // 데이터 로드 후 헬스체크 수행
      await performHealthCheck();
    } catch (error) {
      message.error('API 상태를 불러오는 중 오류가 발생했습니다.');
      console.error('Load API data error:', error);
    } finally {
      setLoading(false);
    }
  }, [performHealthCheck]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadApiData();

    // 30초마다 헬스체크 자동 갱신
    const interval = setInterval(() => {
      performHealthCheck();
    }, 30000);

    return () => clearInterval(interval);
  }, [loadApiData, performHealthCheck]);

  // 홈페이지 링크 열기
  const handleOpenHomepage = (homepage: string) => {
    window.open(homepage, '_blank', 'noopener,noreferrer');
  };

  // API 추가 버튼 클릭
  const handleAddClick = () => {
    setEditingApi(null);
    setModalVisible(true);
  };

  // API 수정 버튼 클릭
  const handleEditClick = (api: ApiItem) => {
    setEditingApi(api);
    setModalVisible(true);
  };

  // API 삭제
  const handleDelete = async (id: number) => {
    try {
      await apiConfigurationService.deleteApiConfiguration(id);
      message.success('삭제가 완료되었습니다.');
      loadApiData(); // 목록 새로고침
    } catch (error: any) {
      console.error('API 삭제 실패:', error);
      message.error(error.response?.data?.message || 'API 삭제 중 오류가 발생했습니다.');
    }
  };

  // 모달 닫기 핸들러
  const handleModalClose = () => {
    setModalVisible(false);
    setEditingApi(null);
  };

  // 모달 성공 콜백
  const handleModalSuccess = () => {
    loadApiData(); // 목록 새로고침
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: 'API 현황' },
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 및 버튼 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <Title level={2} style={{ margin: 0 }}>
          API 현황
          {healthCheckLoading && (
            <ReloadOutlined spin style={{ marginLeft: '12px', fontSize: '20px', color: '#F24822' }} />
          )}
        </Title>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0 }}>
            API 사용 정보
          </Title>
          <Text style={{ color: '#a6a6a6', fontSize: '14px', marginLeft: '16px' }}>
            정상: {Array.from(healthCheckData.values()).filter(v => v === true).length.toString().padStart(1, '0')}개,
            비정상: {Array.from(healthCheckData.values()).filter(v => v === false).length.toString().padStart(1, '0')}개
          </Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadApiData}
            loading={loading}
          >
            새로고침
          </Button>
          {/* <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddClick}
          >
            API 추가
          </Button> */}
        </Space>
      </div>

      {/* 로딩 상태 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text style={{ color: '#ffffff' }}>API 상태를 확인하고 있습니다...</Text>
          </div>
        </div>
      ) : (
        <>
          {/* API 카드 그리드 */}
          <Row gutter={[24, 24]}>
            {apiData.map((item) => {
              // 헬스체크 데이터가 있으면 사용, 없으면 기본값 사용
              const isHealthy = healthCheckData.has(item.id)
                ? healthCheckData.get(item.id)
                : item.isHealthy;

              return (
                <Col key={item.id} xs={24} sm={12} lg={8}>
                  <Card
                    style={{
                      background: '#1f1f1f',
                      border: '1px solid #303030',
                      borderRadius: '8px',
                      height: '320px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    styles={{
                      body: {
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                      }
                    }}
                  >
                    {/* 카드 헤더 - 제목과 상태 아이콘 */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px',
                    }}>
                      <Title
                        level={5}
                        style={{
                          color: '#ffffff',
                          margin: 0,
                          lineHeight: '1.4',
                          flex: 1,
                          marginRight: '12px',
                        }}
                      >
                        {item.title}
                      </Title>
                      {healthCheckLoading ? (
                        <ReloadOutlined
                          spin
                          style={{
                            fontSize: '24px',
                            color: '#F24822'
                          }}
                        />
                      ) : isHealthy ? (
                        <CheckCircleOutlined
                          style={{
                            fontSize: '24px',
                            color: '#52c41a'
                          }}
                        />
                      ) : (
                        <CloseCircleOutlined
                          style={{
                            fontSize: '24px',
                            color: '#ff4d4f'
                          }}
                        />
                      )}
                    </div>

                    {/* 목적 */}
                    <div style={{ marginBottom: '12px' }}>
                      <Text style={{ color: '#a6a6a6', fontSize: '13px', display: 'block', marginBottom: '4px' }}>
                        목적
                      </Text>
                      <Text style={{ color: '#ffffff', fontSize: '14px' }}>
                        {item.description}
                      </Text>
                    </div>

                    {/* Base URL */}
                    <div style={{ marginBottom: '16px' }}>
                      <Text style={{ color: '#a6a6a6', fontSize: '13px', display: 'block', marginBottom: '4px' }}>
                        베이스 URL
                      </Text>
                      <Text
                        style={{
                          color: '#F24822',
                          fontSize: '13px',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.baseUrl}
                      </Text>
                    </div>

                    {/* 카드 하단 - 홈페이지 링크 및 액션 버튼 */}
                    <div style={{
                      marginTop: 'auto',
                      paddingTop: '12px',
                      borderTop: '1px solid #303030',
                    }}>
                      {/* 홈페이지 링크 */}
                      <div
                        onClick={() => handleOpenHomepage(item.homepage)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          cursor: 'pointer',
                          color: '#F24822',
                          transition: 'color 0.3s',
                          marginBottom: '12px',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#F24822';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#F24822';
                        }}
                      >
                        <GlobalOutlined style={{ fontSize: '16px' }} />
                        <Text
                          style={{
                            color: 'inherit',
                            fontSize: '14px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.homepage}
                        </Text>
                      </div>

                      {/* 액션 버튼 - Development Admin만 표시 */}
                      {isDeveloperAdmin && (
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(item);
                            }}
                            style={{ color: '#F24822' }}
                          >
                            수정
                          </Button>
                          <Popconfirm
                            title="API 설정 삭제"
                            description="정말로 이 API 설정을 삭제하시겠습니까?"
                            onConfirm={(e) => {
                              e?.stopPropagation();
                              handleDelete(item.id);
                            }}
                            okText="삭제"
                            cancelText="취소"
                            okButtonProps={{ danger: true }}
                          >
                            {/* <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                          >
                            삭제
                          </Button> */}
                          </Popconfirm>
                        </Space>
                      )}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* 데이터가 없을 때 */}
          {apiData.length === 0 && (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Text style={{ color: '#a6a6a6' }}>등록된 API가 없습니다.</Text>
            </div>
          )}
        </>
      )}

      {/* API 추가/수정 모달 */}
      <ApiConfigurationModal
        visible={modalVisible}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        editingApi={editingApi}
      />
    </Space>
  );
};

export default ApiManagement;